/**
 * Rendu Markdown minimal — juste ce que produit `Report.summaryMd`.
 *
 * Pas de librairie (contrainte de dépendances) et surtout **pas de
 * `dangerouslySetInnerHTML`** : on construit des éléments React, donc rien de ce
 * qui arrive du serveur ne peut devenir du HTML actif. Un rapport est un texte
 * généré, pas un document de confiance arbitraire.
 *
 * Couvre : titres `#`/`##`/`###`, listes `-`, tableaux `|`, règle `---`,
 * paragraphes, et en ligne `**gras**` et `` `code` ``. Tout le reste est rendu
 * littéralement plutôt que perdu.
 */

import type { ReactNode } from 'react';

/** Découpe une ligne en fragments gras / code / texte. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match: RegExpExecArray | null = pattern.exec(text);
  let i = 0;
  while (match !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      out.push(<strong key={`${keyPrefix}-b${i}`}>{token.slice(2, -2)}</strong>);
    } else {
      out.push(<code key={`${keyPrefix}-c${i}`}>{token.slice(1, -1)}</code>);
    }
    last = match.index + token.length;
    i += 1;
    match = pattern.exec(text);
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function splitRow(line: string): string[] {
  return line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** Une ligne de séparation de tableau (`|---|---|`) n'est pas une donnée. */
function isTableSeparator(line: string): boolean {
  return /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && line.includes('-');
}

export function renderMarkdown(source: string): ReactNode[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let table: string[][] = [];
  let key = 0;

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ');
    out.push(<p key={`p${key++}`}>{renderInline(text, `p${key}`)}</p>);
    paragraph = [];
  };
  const flushList = (): void => {
    if (list.length === 0) return;
    out.push(
      <ul key={`u${key++}`}>
        {list.map((item, i) => (
          <li key={i}>{renderInline(item, `u${key}-${i}`)}</li>
        ))}
      </ul>,
    );
    list = [];
  };
  const flushTable = (): void => {
    if (table.length === 0) return;
    const [head, ...body] = table;
    out.push(
      <div className="table-wrap" key={`t${key++}`}>
        <table>
          {head ? (
            <thead>
              <tr>
                {head.map((cell, i) => (
                  <th key={i}>{cell}</th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>{renderInline(cell, `t${key}-${ri}-${ci}`)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
    table = [];
  };
  const flushAll = (): void => {
    flushParagraph();
    flushList();
    flushTable();
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.trim() === '') {
      flushAll();
      continue;
    }
    if (/^\s*\|/.test(line)) {
      flushParagraph();
      flushList();
      if (!isTableSeparator(line)) table.push(splitRow(line));
      continue;
    }
    flushTable();

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      flushAll();
      const level = heading[1]?.length ?? 1;
      const text = heading[2] ?? '';
      const Tag = (level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3') as 'h1' | 'h2' | 'h3';
      out.push(<Tag key={`h${key++}`}>{text}</Tag>);
      continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      flushAll();
      out.push(<hr key={`r${key++}`} />);
      continue;
    }
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      flushParagraph();
      list.push(bullet[1] ?? '');
      continue;
    }
    flushList();
    paragraph.push(line.trim());
  }
  flushAll();
  return out;
}
